import { useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { PageHeader } from "@/components/ui/page-header.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import { IntakeForm } from "@/views/intake-form.js";
import type { Customer } from "@/lib/types.js";

export function CustomersPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const customers = useAsync(() => api.listCustomers(), [api]);

  const filtered = (customers.data?.data ?? []).filter(
    (c) =>
      !filter ||
      c.name.toLowerCase().includes(filter.toLowerCase()) ||
      c.phone_e164?.includes(filter) ||
      c.email?.toLowerCase().includes(filter.toLowerCase())
  );

  useKeyboardShortcuts({
    c: () => setCreating(true),
    j: () => setSelectedId(moveSelection(filtered, selectedId, 1)),
    k: () => setSelectedId(moveSelection(filtered, selectedId, -1)),
    e: () => {
      const row = filtered.find((c) => c.id === selectedId);
      if (row) setEditing(row);
    }
  });

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Catalog"
        title="Customers"
        description="People who book or receive services. Profile updates affect new bookings only."
        actions={
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, phone, email…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-64 pl-8"
              />
            </div>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add customer
            </Button>
          </>
        }
      />
      {customers.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<Customer>
          rows={filtered}
          getRowKey={(c) => c.id}
          selectedRowKey={selectedId}
          onRowClick={(c) => setSelectedId(c.id)}
          columns={[
            { header: "Name", cell: (c) => c.name },
            { header: "Phone", cell: (c) => c.phone_e164 ?? "—" },
            { header: "Email", cell: (c) => c.email ?? "—" },
            { header: "Address", cell: (c) => c.address ?? "—" },
            {
              header: "",
              className: "w-20 text-right",
              cell: (c) => (
                <div className="flex justify-end gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(c);
                    }}
                    aria-label={`Edit ${c.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Erase ${c.name}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.eraseCustomer(c.id);
                        toast({ title: "Erased", variant: "success" });
                        customers.reload();
                      } catch (error) {
                        toast({
                          title: "Could not erase",
                          description: error instanceof Error ? error.message : String(error),
                          variant: "destructive"
                        });
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )
            }
          ]}
        />
      )}
      <CustomerDialog
        open={creating}
        onOpenChange={setCreating}
        mode="create"
        onSaved={() => {
          customers.reload();
          setCreating(false);
        }}
      />
      <CustomerDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        mode="edit"
        initial={editing}
        onSaved={() => {
          customers.reload();
          setEditing(null);
        }}
      />
    </div>
  );
}

function moveSelection<T extends { id: string }>(
  rows: T[],
  current: string | null,
  delta: 1 | -1
): string | null {
  if (rows.length === 0) return null;
  const idx = current ? rows.findIndex((r) => r.id === current) : -1;
  const nextIdx = idx === -1 ? (delta === 1 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, idx + delta));
  return rows[nextIdx]!.id;
}

function CustomerDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  initial?: Customer | null;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New customer" : `Edit ${initial?.name ?? "customer"}`}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const verticalFields: Record<string, unknown> = {};
            const body: Partial<Customer> & { name?: string } = { vertical_fields: verticalFields };
            for (const [key, value] of form.entries()) {
              if (typeof value !== "string") continue;
              if (key.startsWith("vertical:")) {
                verticalFields[key.slice("vertical:".length)] = value;
              } else if (key === "name" || key === "phone_e164" || key === "email" || key === "address") {
                (body as Record<string, unknown>)[key] = value;
              }
            }
            if (mode === "create" && !body.name) return;
            setSubmitting(true);
            try {
              if (mode === "edit" && initial) {
                await api.updateCustomer(initial.id, body);
                toast({ title: "Customer updated", variant: "success" });
              } else if (body.name) {
                await api.createCustomer({ ...body, name: body.name });
                toast({ title: "Customer created", variant: "success" });
              }
              onSaved();
            } catch (error) {
              toast({
                title: "Could not save",
                description: error instanceof Error ? error.message : String(error),
                variant: "destructive"
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <IntakeForm initial={(initial ?? {}) as Record<string, unknown>} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
